import React, { useState } from "react";
import { TASK_LABEL, TASKS_LABEL } from "./taskCopy";
import { WORK_TYPES } from "./workTypeModel";
import AgentAvailabilityControl from "./AgentAvailabilityControl";
import {
  agentWorkflowSummary,
  founderReadinessFromRoadmapItem,
  importanceLabel,
  reviewSummaryForCollapsed,
  workTypeDisplay,
  whyOnRoadmap,
} from "./founderRoadmapCopy";
import "./RoadmapTaskList.css";

function RoadmapTaskRow({
  item,
  position,
  mutateAllowed,
  busy,
  linkItemId,
  briefsLoading,
  availableBriefs,
  selectedBriefId,
  onSelectedBriefId,
  onOpenTask,
  onCreateTask,
  onEdit,
  onDelete,
  onLink,
  onUnlink,
  onOpenLinkPicker,
  onCloseLinkPicker,
  onMove,
  onChangeWorkType,
  onAuthorize,
  onRevoke,
  onAnswer,
  onStop,
  onReview,
}) {
  const [expanded, setExpanded] = useState(false);
  const type = workTypeDisplay(item);
  const readiness = founderReadinessFromRoadmapItem(item);
  const reviewLine = reviewSummaryForCollapsed(item);
  const agent = agentWorkflowSummary(item);
  const titleNode =
    item.workBriefId && typeof onOpenTask === "function" ? (
      <button
        type="button"
        className="roadmap-item-title-link"
        onClick={() => onOpenTask(item.workBriefId)}
        data-testid={`roadmap-open-brief-${item.id}`}
      >
        {item.title}
      </button>
    ) : (
      <h4 className="roadmap-item-title">{item.title}</h4>
    );

  return (
    <li
      className={`roadmap-item roadmap-task-row${expanded ? " is-open" : ""}`}
      data-testid={`roadmap-item-${item.id}`}
      data-readiness={readiness.code}
      tabIndex={mutateAllowed ? 0 : undefined}
      onKeyDown={(event) => {
        if (!mutateAllowed) return;
        if (event.key === "ArrowUp") {
          event.preventDefault();
          onMove(item, -1);
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          onMove(item, 1);
        }
      }}
    >
      <span className="roadmap-task-priority" data-testid={`roadmap-priority-${item.id}`}>
        {position}
      </span>

      <div className="roadmap-task-copy">
        <div className="roadmap-task-topline">
          <span className="roadmap-work-type" data-testid={`roadmap-work-type-${item.id}`}>
            <span className="roadmap-work-type-icon" aria-hidden="true">
              {type.icon}
            </span>
            <span>{type.label}</span>
          </span>
          <span
            className={`roadmap-readiness roadmap-readiness-${readiness.code}`}
            data-testid={`roadmap-readiness-${item.id}`}
          >
            {readiness.label}
          </span>
          <span className="roadmap-importance" data-testid={`roadmap-importance-${item.id}`}>
            {importanceLabel(item.priority)}
          </span>
        </div>
        {titleNode}
        {item.outcome ? <p className="roadmap-item-summary">{item.outcome}</p> : null}
        <p className="roadmap-why">{whyOnRoadmap(item, position)}</p>
        {reviewLine ? (
          <p className="roadmap-item-review" data-testid={`roadmap-review-${item.id}`}>
            {reviewLine}
          </p>
        ) : (
          <span data-testid={`roadmap-review-${item.id}`} hidden>
            No review status
          </span>
        )}
      </div>

      <div className="roadmap-task-controls">
        {mutateAllowed ? (
          <div className="roadmap-move-group" aria-label={`Reorder ${item.title}`}>
            <button
              type="button"
              className="roadmap-icon-button"
              onClick={() => onMove(item, -1)}
              aria-label={`Move ${item.title} up`}
              data-testid={`roadmap-move-up-${item.id}`}
              disabled={busy || position <= 1}
            >
              ↑
            </button>
            <button
              type="button"
              className="roadmap-icon-button"
              onClick={() => onMove(item, 1)}
              aria-label={`Move ${item.title} down`}
              data-testid={`roadmap-move-down-${item.id}`}
              disabled={busy}
            >
              ↓
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className="roadmap-icon-button"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} details for ${item.title}`}
          onClick={() => setExpanded((value) => !value)}
          data-testid={`roadmap-expand-${item.id}`}
        >
          {expanded ? "▾" : "›"}
        </button>
      </div>

      {expanded ? (
        <div className="roadmap-task-detail" data-testid={`roadmap-details-${item.id}`}>
          {(item.owner || item.blocker) && (
            <p className="roadmap-item-meta">
              {item.owner ? `Owner: ${item.owner}` : null}
              {item.owner && item.blocker ? " · " : null}
              {item.blocker ? `Blocker: ${item.blocker}` : null}
            </p>
          )}

          {item.linkedAcceptanceReview?.aggregate ? (
            <p className="roadmap-item-review-detail">
              Review evidence: Met {item.linkedAcceptanceReview.aggregate.met} · Partial{" "}
              {item.linkedAcceptanceReview.aggregate.partiallyMet} · Cannot verify{" "}
              {item.linkedAcceptanceReview.aggregate.cannotVerify} · Not met{" "}
              {item.linkedAcceptanceReview.aggregate.notMet}
            </p>
          ) : null}

          <div className="roadmap-agent-workflow" data-testid={`agent-status-${item.id}`}>
            <strong>Agent workflow</strong>
            <p data-testid={`agent-workflow-line-${item.id}`}>
              {agent.visible ? agent.line : "Not currently available to agents."}
            </p>
            {agent.nextActor ? <p>Next: {agent.nextActor}</p> : null}
            {item.agentExecution?.customerState === "needs_your_answer" && item.agentExecution?.question ? (
              <p data-testid={`agent-question-${item.id}`}>“{item.agentExecution.question}”</p>
            ) : null}
            <div className="roadmap-task-detail-actions">
              {mutateAllowed ? (
                <AgentAvailabilityControl
                  item={item}
                  mutateAllowed={mutateAllowed}
                  busy={busy}
                  onAuthorize={onAuthorize}
                  onRevoke={onRevoke}
                />
              ) : null}
              {mutateAllowed && item.agentExecution?.customerState === "needs_your_answer" ? (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onAnswer(item)}
                  data-testid={`agent-answer-${item.id}`}
                >
                  Answer question
                </button>
              ) : null}
              {mutateAllowed && item.agentExecution?.customerState === "working_now" ? (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onStop(item)}
                  data-testid={`agent-stop-${item.id}`}
                >
                  Request stop
                </button>
              ) : null}
              {item.agentExecution?.customerState === "ready_for_review" ? (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onReview(item)}
                  data-testid={`agent-review-${item.id}`}
                >
                  Review delivered work
                </button>
              ) : null}
            </div>
          </div>

          {mutateAllowed && !item.workBriefId ? (
            <button
              type="button"
              className="secondary-button"
              onClick={() => onCreateTask(item)}
              disabled={busy}
              data-testid={`roadmap-create-brief-${item.id}`}
            >
              {busy ? "Creating…" : `Create ${TASK_LABEL}`}
            </button>
          ) : null}

          {linkItemId === item.id && (
            <div className="roadmap-link-picker" data-testid={`roadmap-link-picker-${item.id}`}>
              {briefsLoading ? (
                <p>Loading {TASKS_LABEL}…</p>
              ) : availableBriefs.length === 0 ? (
                <p>No available {TASKS_LABEL} to link.</p>
              ) : (
                <>
                  <select
                    data-testid={`roadmap-link-select-${item.id}`}
                    value={selectedBriefId}
                    onChange={(e) => onSelectedBriefId(e.target.value)}
                  >
                    <option value="">Select a {TASK_LABEL}…</option>
                    {availableBriefs.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {(plan.featureRequestText || plan._id).slice(0, 80)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={!selectedBriefId || busy}
                    onClick={onLink}
                    data-testid={`roadmap-link-confirm-${item.id}`}
                  >
                    Link
                  </button>
                </>
              )}
              <button type="button" className="secondary-button" onClick={onCloseLinkPicker}>
                Cancel
              </button>
            </div>
          )}

          {mutateAllowed ? (
            <details className="roadmap-item-more" data-testid={`roadmap-more-${item.id}`}>
              <summary>More actions</summary>
              <div className="roadmap-item-secondary-actions">
                <label>
                  Work type
                  <select
                    value={item.workType || ""}
                    onChange={(e) => onChangeWorkType(item, e.target.value || null)}
                    data-testid={`roadmap-work-type-select-${item.id}`}
                  >
                    <option value="">Needs a type</option>
                    {WORK_TYPES.map((row) => (
                      <option key={row.value} value={row.value}>
                        {row.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onEdit(item)}
                  data-testid={`roadmap-edit-${item.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onDelete(item)}
                  disabled={busy}
                  data-testid={`roadmap-delete-${item.id}`}
                >
                  Delete
                </button>
                {item.workBriefId ? (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => onUnlink(item)}
                    disabled={busy}
                    data-testid={`roadmap-unlink-${item.id}`}
                  >
                    Unlink {TASK_LABEL}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onOpenLinkPicker(item)}
                  disabled={busy}
                  data-testid={`roadmap-link-${item.id}`}
                >
                  Link existing
                </button>
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function RoadmapTaskList({ items, startPosition = 1, ...rowProps }) {
  if (!items.length) {
    return <p className="roadmap-lane-empty">No items</p>;
  }
  return (
    <ul className="roadmap-list roadmap-task-list" data-testid="roadmap-task-list">
      {items.map((item, index) => (
        <RoadmapTaskRow
          key={item.id}
          item={item}
          position={startPosition + index}
          {...rowProps}
        />
      ))}
    </ul>
  );
}

export default RoadmapTaskList;
export { RoadmapTaskRow };
